import { useCallback, useMemo } from "react";
import { roundToCents } from "@common/helpers/monetary";
import { MatchableItem, MatchableTransaction, MatchItemType } from "../types";
import {
    getAllLineItems,
    getIsAllocationSaveEnabled,
} from "./allocationHelpers";
import { buildAllocationBody } from "./buildAllocationBody";
import { useAllocationLedgerContext } from "./useAllocationLedgerContext";
import {
    resolveRemainingLineDefaults,
    useRemainingLines,
} from "./useRemainingLines";

interface Params {
    sourceTransaction: MatchableTransaction | null;
    sourceItem: MatchableItem | null;
    selectedItems: MatchableItem[];
    paymentAmounts: Record<string, number>;
}

export function useTransactionAllocation({
    sourceTransaction,
    sourceItem,
    selectedItems,
    paymentAmounts,
}: Params) {
    const ledger = useAllocationLedgerContext(sourceTransaction);
    const {
        entityId,
        hasAccrualLedger,
        accounts,
        primaryAccrualLedgerId,
        primaryCashLedgerId,
    } = ledger;

    const remainingLineDefaults = useMemo(() => {
        if (!sourceTransaction) {
            return {
                cashAccountId: null,
                accrualAccountId: null,
                classIds: [],
            };
        }
        return resolveRemainingLineDefaults({
            transaction: sourceTransaction,
            entityId,
            accounts,
        });
    }, [accounts, entityId, sourceTransaction]);

    const amountToBeAllocated = Math.abs(sourceTransaction?.amount ?? 0);

    const allLineEntries = useMemo(
        () => getAllLineItems(selectedItems),
        [selectedItems],
    );

    const remainingState = useRemainingLines({
        sourceTransaction,
        defaultAccountId: remainingLineDefaults.accrualAccountId,
        defaultCashAccountId: remainingLineDefaults.cashAccountId,
        financialAccountId: sourceItem?.financialAccountId ?? null,
        sourceClassIds: remainingLineDefaults.classIds,
    });

    const lineItemsTotal = useMemo(
        () =>
            roundToCents(
                allLineEntries.reduce(
                    (sum, { lineItem }) =>
                        sum + (paymentAmounts[lineItem.id] ?? 0),
                    0,
                ),
            ),
        [allLineEntries, paymentAmounts],
    );

    const transactionItemsTotal = useMemo(
        () =>
            roundToCents(
                selectedItems
                    .filter((item) => item.type === MatchItemType.TRANSACTION)
                    .reduce((sum, item) => sum + Math.abs(item.amount), 0),
            ),
        [selectedItems],
    );

    const allocatedTotal = useMemo(
        () =>
            roundToCents(
                lineItemsTotal + transactionItemsTotal + remainingState.total,
            ),
        [lineItemsTotal, transactionItemsTotal, remainingState.total],
    );

    const remainingAmount = useMemo(
        () => roundToCents(amountToBeAllocated - allocatedTotal),
        [amountToBeAllocated, allocatedTotal],
    );

    const isAllocationValid = useMemo(
        () =>
            getIsAllocationSaveEnabled({
                hasAccrualLedger,
                allocatedTotal,
                amountToBeAllocated,
            }),
        [allocatedTotal, amountToBeAllocated, hasAccrualLedger],
    );

    const showRemaining = useCallback(() => {
        remainingState.show(
            roundToCents(
                amountToBeAllocated - lineItemsTotal - transactionItemsTotal,
            ),
        );
    }, [
        amountToBeAllocated,
        lineItemsTotal,
        transactionItemsTotal,
        remainingState,
    ]);

    const body = useMemo(
        () =>
            buildAllocationBody({
                sourceTransaction: {
                    id: sourceTransaction?.id ?? 0,
                    categoryId: sourceTransaction?.categoryId,
                },
                selectedItems,
                paymentAmounts,
                remainingLines: remainingState.lines,
                primaryAccrualLedgerId,
                primaryCashLedgerId,
            }),
        [
            sourceTransaction?.id,
            sourceTransaction?.categoryId,
            selectedItems,
            paymentAmounts,
            remainingState.lines,
            primaryAccrualLedgerId,
            primaryCashLedgerId,
        ],
    );

    return {
        ...ledger,
        remainingState,
        lineItemsTotal,
        transactionItemsTotal,
        allocatedTotal,
        amountToBeAllocated,
        remainingAmount,
        isAllocationValid,
        showRemaining,
        body,
        hasRemainingAllocations: body.remaining !== undefined,
    };
}

export type TransactionAllocation = ReturnType<typeof useTransactionAllocation>;
